import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { PhoneInput } from '../components/PhoneInput';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { saveErrorText } from '../utils/apiError';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { inspectorApi } from '../services/api';
import {
  normalizeVat,
  isValidVatSE,
  vatFromOrgNumber,
  vatMatchesOrg
} from '../utils/vat';
import { ProviderThemeEditor } from '../components/company-theme/ProviderThemeEditor';

interface InspectorFormData {
  name: string;
  organizationNumber: string;
  inspectorCompanyName: string;
  email: string;
  phone: string;
  vatNumber: string;
  notVatRegistered: boolean;
  website: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  title: string;
  experienceYears: string;
  specialties: string;
  responseTime: string;
  hourlyRate: string;
  fixedPriceAvailable: boolean;
  geographicArea: string;
  languages: string;
  initialMeetingFree: boolean;
  availability: boolean;
  publishedToMarketplace: boolean;
  leadTime: string;
  notes: string;
  inspectorPresentation: string;
}

function inspectorToForm(inspector: any): InspectorFormData {
  return {
    name: inspector.name || '',
    organizationNumber: inspector.organizationNumber || '',
    inspectorCompanyName: inspector.inspectorCompanyName || '',
    email: inspector.email || '',
    phone: inspector.phone || '',
    vatNumber: inspector.vatNumber || '',
    notVatRegistered: Boolean(inspector.notVatRegistered),
    website: inspector.website || '',
    address: inspector.address || '',
    postalCode: inspector.postalCode || '',
    city: inspector.city || '',
    country: inspector.country || 'Sverige',
    title: inspector.title || '',
    experienceYears: String(inspector.experienceYears || ''),
    specialties: (inspector.specialties || []).join(', '),
    responseTime: inspector.responseTime || '',
    hourlyRate: inspector.hourlyRate || '',
    fixedPriceAvailable: inspector.fixedPriceAvailable || false,
    geographicArea: inspector.geographicArea || '',
    languages: (inspector.languages || []).join(', '),
    initialMeetingFree: inspector.initialMeetingFree || false,
    availability: inspector.availability !== undefined ? inspector.availability : true,
    publishedToMarketplace: inspector.publishedToMarketplace !== undefined ? inspector.publishedToMarketplace : true,
    leadTime: inspector.leadTime || '',
    notes: inspector.notes || '',
    inspectorPresentation: inspector.inspectorPresentation || ''
  };
}

export function InspectorSettings() {
  const { t } = useTranslation();
  const [inspector, setInspector] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<InspectorFormData>(inspectorToForm({}));
  const [activeTab, setActiveTab] = useState<'overview' | 'practical' | 'theme'>('overview');

  useEffect(() => {
    loadInspector();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInspector = async () => {
    try {
      const data = await inspectorApi.getInspector();
      setInspector(data);
      setFormData(inspectorToForm(data));
    } catch (error) {
      console.error('Failed to load inspector data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field === 'notVatRegistered') {
        return { ...prev, notVatRegistered: value, vatNumber: value ? '' : prev.vatNumber };
      }
      const next = { ...prev, [field]: field === 'vatNumber' ? normalizeVat(value) : value };
      if (field === 'organizationNumber' && !prev.vatNumber && !prev.notVatRegistered) {
        const derived = vatFromOrgNumber(value);
        if (derived) next.vatNumber = derived;
      }
      return next;
    });
  };

  const vatError =
    isEditing && !formData.notVatRegistered && formData.vatNumber && !isValidVatSE(formData.vatNumber)
      ? t('common.vatSeError')
      : '';
  const vatWarning =
    isEditing && !formData.notVatRegistered && !vatError && formData.vatNumber &&
    !vatMatchesOrg(formData.vatNumber, formData.organizationNumber)
      ? t('common.vatOrgMismatch')
      : '';

  const handleEdit = () => { setIsEditing(true); setMessage(null); };
  const handleCancelEdit = () => { if (inspector) setFormData(inspectorToForm(inspector)); setIsEditing(false); };

  const handleSave = async () => {
    if (vatError) {
      setMessage({ type: 'error', text: t('common.vatSeError') });
      return;
    }
    setIsSaving(true);
    try {
      const { name: _verifiedName, email: _verifiedEmail, ...details } = formData;
      await inspectorApi.updateInspector({
        ...details,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean) as any,
        languages: formData.languages.split(',').map(s => s.trim()).filter(Boolean) as any,
        experienceYears: Number(formData.experienceYears) || 0 as any,
        vatNumber: formData.notVatRegistered ? null : (formData.vatNumber || null),
        notVatRegistered: formData.notVatRegistered,
        website: formData.website || null,
        inspectorPresentation: formData.inspectorPresentation || null
      } as any);
      setIsEditing(false);
      setMessage({ type: 'success', text: t('settings.inspectorDetails.saveSuccess') });
      // The saved details are read back in the background (ClickUp 869fgxq45).
      void inspectorApi.getInspector()
        .then(updated => {
          setInspector(updated);
          setFormData(inspectorToForm(updated));
        })
        .catch(error => console.error('Failed to reload details:', error));
    } catch (error) {
      console.error('Failed to save inspector:', error);
      setMessage({ type: 'error', text: saveErrorText(t, error, 'settings.inspectorDetails.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (<DashboardLayout><div className="d-flex justify-content-center align-items-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t('common.loading')}</span></div></div></DashboardLayout>);
  }

  if (!inspector) {
    return (<DashboardLayout><div className="alert alert-danger">{t('common.error')}</div></DashboardLayout>);
  }

  // Save/Cancel stay at the bottom of the section (edit mode only). The edit
  // ("Ändra") button now lives in the section header (see sectionHeader).
  const buttons = isEditing ? (
    <div className="d-flex justify-content-end gap-2 mt-4">
      <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>{t('common.cancel')}</button>
      <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
        {isSaving && <span className="spinner-border spinner-border-sm me-2"></span>}
        <i className="feather-save me-2"></i>{t('common.save')}
      </button>
    </div>
  ) : null;

  // Section header with the title on the left and the "Ändra" button top-right
  // (view mode only). flex-wrap keeps them from colliding on small screens.
  const sectionHeader = (title: string) => (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
      <h5 className="fw-bold mb-0">{title}</h5>
      {!isEditing && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleEdit}
          aria-label={t('common.change', 'Ändra')}
        >
          <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
        </button>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('settings.inspectorDetails.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('settings.inspectorDetails.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('settings.inspectorDetails.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-4`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-4" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <ul className="nav nav-tabs nav-tabs-custom-style flex-nowrap" role="tablist" style={{ minWidth: 'max-content' }}>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              <i className="feather-clipboard d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('settings.inspectorDetails.tabOverview', 'Besiktningsmansöversikt')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'practical' ? 'active' : ''}`} onClick={() => setActiveTab('practical')}>
              <i className="feather-info d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</span>
            </button>
          </li>
          {/* Logo and colours for the expert card (ClickUp 869fa6m3u). */}
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'theme' ? 'active' : ''}`} onClick={() => setActiveTab('theme')} data-testid="provider-theme-tab">
              <i className="feather-droplet d-sm-none me-0" aria-hidden="true"></i>
              <span className="d-none d-sm-inline">{t('settings.theme.title')}</span>
              <span className="visually-hidden d-sm-none">{t('settings.theme.title')}</span>
            </button>
          </li>
        </ul>
      </div>

      {activeTab === 'overview' && (
        <Card>
          <div className="card-body">
            {sectionHeader(t('settings.inspectorDetails.tabOverview', 'Besiktningsmansöversikt'))}
            {/* === Grunduppgifter === */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.inspectorNumber', 'Besiktningsnummer')}</label>
                <input type="text" className="form-control" value={inspector.inspectorNumber ?? ''} disabled />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.verifiedInspector', 'Status')}</label>
                <div><span className={`badge bg-${inspector.verifiedInspector === 'Verifierad' ? 'success' : 'secondary'}`}>{inspector.verifiedInspector || '-'}</span></div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.name', 'Namn')}</label>
                <input type="text" className="form-control" value={inspector.name || ''} readOnly aria-label={t('auth.bankid.verifiedName')} />
                <div className="form-text">{t('auth.bankid.nameLocked')}</div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.organizationNumber', 'Org.Nr')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.organizationNumber : (inspector.organizationNumber || '')} disabled={!isEditing} onChange={e => handleChange('organizationNumber', e.target.value)} />
                <TestOrgNumberButton disabled={!isEditing} onGenerated={value => setFormData(previous => withTestOrgNumber(previous, value))} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.inspectorCompanyName', 'Bolagsnamn')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.inspectorCompanyName : (inspector.inspectorCompanyName || '')} disabled={!isEditing} onChange={e => handleChange('inspectorCompanyName', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.phone', 'Telefon')}</label>
                <PhoneInput name="phone" className="form-control" value={isEditing ? formData.phone : (inspector.phone || '')} disabled={!isEditing} onChange={e => handleChange('phone', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.vatNumber', 'Momsregistreringsnummer')}</label>
                <input type="text" className={`form-control ${vatError ? 'is-invalid' : ''}`} value={isEditing ? formData.vatNumber : (inspector.vatNumber || '')} disabled={!isEditing || formData.notVatRegistered} placeholder="SE556677889901" onChange={e => handleChange('vatNumber', e.target.value)} />
                {vatError && <div className="invalid-feedback d-block">{vatError}</div>}
                {vatWarning && <div className="text-warning small mt-1"><i className="feather-alert-triangle me-1"></i>{vatWarning}</div>}
                {isEditing && (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="inspectorNotVatRegistered" checked={formData.notVatRegistered} onChange={e => handleChange('notVatRegistered', e.target.checked)} />
                    <label className="form-check-label" htmlFor="inspectorNotVatRegistered">{t('onboarding.providerProfile.notVatRegistered', 'Ej momsregistrerad')}</label>
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.email', 'E-post')}</label>
                <input type="email" className="form-control" value={isEditing ? formData.email : (inspector.email || '')} readOnly />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.website', 'Webbplats')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.website : (inspector.website || '')} disabled={!isEditing} onChange={e => handleChange('website', e.target.value)} />
              </div>
            </div>

            {/* === Adress === */}
            <hr className="my-3" />
            <h6 className="fw-bold mb-3">{t('inspectors.columns.address', 'Adress')}</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <AddressAutocomplete
                  name="address"
                  label={t('inspectors.columns.address', 'Gatuadress')}
                  onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                  type="text"
                  value={isEditing ? formData.address : (inspector.address || '')}
                  disabled={!isEditing}
                  onChange={e => handleChange('address', e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.postalCode', 'Postkod')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.postalCode : (inspector.postalCode || '')} disabled={!isEditing} onChange={e => handleChange('postalCode', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.city', 'Stad')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.city : (inspector.city || '')} disabled={!isEditing} onChange={e => handleChange('city', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.country', 'Land')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.country : (inspector.country || '')} disabled={!isEditing} onChange={e => handleChange('country', e.target.value)} />
              </div>
            </div>

            {/* === Besiktningsmanspresentation === */}
            <hr className="my-3" />
            <div className="mb-3">
              <label className="form-label">{t('inspectors.columns.inspectorPresentation', 'Besiktningsmanspresentation')}</label>
              {isEditing ? (
                <>
                  <ReactQuill theme="snow" value={formData.inspectorPresentation} onChange={v => handleChange('inspectorPresentation', v)} style={{ height: '150px' }} />
                  <div style={{ height: '45px' }}></div>
                </>
              ) : inspector.inspectorPresentation ? (
                <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                  <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: inspector.inspectorPresentation }} />
                </div>
              ) : (
                <input type="text" className="form-control" value="" disabled />
              )}
            </div>

            {/* === Anteckningar === */}
            <div className="mb-3">
              <label className="form-label">{t('inspectors.columns.notes', 'Anteckningar')}</label>
              {isEditing ? (
                <>
                  <ReactQuill theme="snow" value={formData.notes} onChange={v => handleChange('notes', v)} style={{ height: '150px' }} />
                  <div style={{ height: '45px' }}></div>
                </>
              ) : inspector.notes ? (
                <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                  <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: inspector.notes }} />
                </div>
              ) : (
                <input type="text" className="form-control" value="" disabled />
              )}
            </div>

            {/* === Knappar === */}
            {buttons}
          </div>
        </Card>
      )}

      {activeTab === 'theme' && (
        <Card>
          <div className="card-body">
            <ProviderThemeEditor type="inspector" profile={inspector ? { geographicArea: inspector.geographicArea, languages: inspector.languages ?? [] } : null} />
          </div>
        </Card>
      )}

      {activeTab === 'practical' && (
        <Card>
          <div className="card-body">
            {sectionHeader(t('marketplace.provider.practicalInfo', 'Praktisk information'))}
            {/* === Marknadsplatsinformation === */}
            <h6 className="fw-bold mb-3">{t('marketplace.provider.practicalInfo', 'Marknadsplatsinformation')}</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.title', 'Titel')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.title : (inspector.title || '')} disabled={!isEditing} onChange={e => handleChange('title', e.target.value)} placeholder={t('inspectorSettings.titlePlaceholder')} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.experienceYears', 'Erfarenhetsår')}</label>
                <input type="number" className="form-control" value={isEditing ? formData.experienceYears : (inspector.experienceYears ?? 0)} disabled={!isEditing} onChange={e => handleChange('experienceYears', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.specialties', 'Specialistområden')}</label>
                {isEditing ? (
                  <>
                    <input type="text" className="form-control" value={formData.specialties} onChange={e => handleChange('specialties', e.target.value)} placeholder={t('inspectorSettings.specialtiesPlaceholder')} />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </>
                ) : (
                  <div>{(inspector.specialties || []).length > 0 ? (inspector.specialties || []).map((s: string, i: number) => <span key={i} className="badge bg-primary me-1 mb-1">{s}</span>) : <span className="text-muted">—</span>}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.languages', 'Språk')}</label>
                {isEditing ? (
                  <>
                    <input type="text" className="form-control" value={formData.languages} onChange={e => handleChange('languages', e.target.value)} placeholder={t('inspectorSettings.languagesPlaceholder')} />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </>
                ) : (
                  <div>{(inspector.languages || []).length > 0 ? (inspector.languages || []).map((l: string, i: number) => <span key={i} className="badge bg-info me-1 mb-1">{l}</span>) : <span className="text-muted">—</span>}</div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.hourlyRate', 'Timarvode')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.hourlyRate : (inspector.hourlyRate || '')} disabled={!isEditing} onChange={e => handleChange('hourlyRate', e.target.value)} placeholder={t('inspectorSettings.hourlyRatePlaceholder')} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.responseTime', 'Svarstid')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.responseTime : (inspector.responseTime || '')} disabled={!isEditing} onChange={e => handleChange('responseTime', e.target.value)} placeholder={t('inspectorSettings.responseTimePlaceholder')} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.geographicArea', 'Geografiskt område')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.geographicArea : (inspector.geographicArea || '')} disabled={!isEditing} onChange={e => handleChange('geographicArea', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.leadTime', 'Ledtid')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.leadTime : (inspector.leadTime || '')} disabled={!isEditing} onChange={e => handleChange('leadTime', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('inspectors.columns.fixedPriceAvailable', 'Fast pris möjligt')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="fixedPrice" checked={formData.fixedPriceAvailable} onChange={e => handleChange('fixedPriceAvailable', e.target.checked)} />
                    <label className="form-check-label" htmlFor="fixedPrice">{formData.fixedPriceAvailable ? t('inspectorSettings.yes') : t('inspectorSettings.no')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.fixedPriceAvailable ? 'success' : 'secondary'}`}>{inspector.fixedPriceAvailable ? t('inspectorSettings.yes') : t('inspectorSettings.no')}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('inspectors.columns.initialMeetingFree', 'Gratis inledande möte')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="initialMeeting" checked={formData.initialMeetingFree} onChange={e => handleChange('initialMeetingFree', e.target.checked)} />
                    <label className="form-check-label" htmlFor="initialMeeting">{formData.initialMeetingFree ? t('inspectorSettings.yes') : t('inspectorSettings.no')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.initialMeetingFree ? 'success' : 'secondary'}`}>{inspector.initialMeetingFree ? t('inspectorSettings.yes') : t('inspectorSettings.no')}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('inspectors.columns.availability', 'Tillgänglig')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="availability" checked={formData.availability} onChange={e => handleChange('availability', e.target.checked)} />
                    <label className="form-check-label" htmlFor="availability">{formData.availability ? t('inspectorSettings.yes') : t('inspectorSettings.no')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.availability ? 'success' : 'secondary'}`}>{inspector.availability ? t('inspectorSettings.yes') : t('inspectorSettings.no')}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('providerSettings.marketplaceVisibility.label')}</label>
                {isEditing ? (
                  <div className="form-check form-switch mt-2">
                    <input type="checkbox" role="switch" className="form-check-input" id="publishedToMarketplace" checked={formData.publishedToMarketplace} onChange={e => handleChange('publishedToMarketplace', e.target.checked)} />
                    <label className="form-check-label" htmlFor="publishedToMarketplace">{formData.publishedToMarketplace ? t('providerSettings.marketplaceVisibility.visible') : t('providerSettings.marketplaceVisibility.hidden')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.publishedToMarketplace ? 'success' : 'secondary'}`}>{inspector.publishedToMarketplace ? t('providerSettings.marketplaceVisibility.visible') : t('providerSettings.marketplaceVisibility.hidden')}</span></div>
                )}
              </div>
            </div>

            {/* === Knappar === */}
            {buttons}
          </div>
        </Card>
      )}
    </DashboardLayout>
  );
}
